import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 * @property {HTMLElement} [cartBubbleAccessibleCount] - The count read by assistive technology.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];
  #cartUpdateRevision = 0;

  /** @type {number} */
  get currentCartCount() {
    return Number(this.refs.cartBubble.dataset.count ?? this.refs.cartBubbleCount.textContent) || 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubble.dataset.count = String(value);
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
    if (this.refs.cartBubbleAccessibleCount) {
      this.refs.cartBubbleAccessibleCount.textContent = String(value);
    }
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.refreshCartCount(++this.#cartUpdateRevision);
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const revision = ++this.#cartUpdateRevision;
    const data = event.detail.data ?? {};
    const actualCount = event.detail.resource?.item_count;

    // An inventory error may add nothing or only part of the requested amount.
    // Read the cart instead of adding the attempted quantity to the bubble.
    if (data.didError) {
      await this.refreshCartCount(revision);
      return;
    }

    if (Number.isInteger(actualCount) && actualCount >= 0) {
      this.renderCartBubble(actualCount, false);
      return;
    }

    const itemCount = data.itemCount;
    if (!Number.isInteger(itemCount) || itemCount < 0) {
      await this.refreshCartCount(revision);
      return;
    }
    this.renderCartBubble(itemCount, data.source === 'product-form-component');
  };

  /** @param {number} revision - Ignore responses superseded by a newer cart update. */
  refreshCartCount = async (revision) => {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return;
      const cart = await response.json();
      if (revision !== this.#cartUpdateRevision || !Number.isInteger(cart.item_count) || cart.item_count < 0) return;
      this.renderCartBubble(cart.item_count, false, false);
    } catch (_) {
      // Keep the last known count if the refresh fails; never invent a new total.
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    const totalCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;
    if (!Number.isInteger(totalCount) || totalCount < 0) return;

    this.refs.cartBubbleCount.classList.toggle('hidden', totalCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', totalCount === 0);

    this.currentCartCount = totalCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', totalCount > 0);

    try {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({ value: String(totalCount), timestamp: Date.now() })
      );
    } catch (_) {
      // Cart updates must still work when browser storage is disabled.
    }

    if (!animate || totalCount === 0 || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    let sessionStorageCount;
    try {
      sessionStorageCount = sessionStorage.getItem('cart-count');
    } catch (_) {
      return;
    }

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
